import React, { useState, useEffect } from "react";
import axios from "axios";
import { Heart, Briefcase, PartyPopper, UtensilsCrossed, ChefHat, Users, Star, Calendar, Wine, Gift, ArrowRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ICON_MAP = {
  heart: Heart, briefcase: Briefcase, party: PartyPopper, utensils: UtensilsCrossed,
  chef: ChefHat, users: Users, star: Star, calendar: Calendar, wine: Wine, gift: Gift,
};

export default function CateringSection() {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/catering`)
      .then((res) => { if (res.data && res.data.info) setData(res.data); })
      .catch(() => {});
  }, []);

  // Fallback to translations if API not yet loaded
  const info = data?.info;
  const badge = info ? (lang === "en" && info.badge_en ? info.badge_en : info.badge) : t("cateringSection.badge");
  const title = info ? (lang === "en" && info.title_en ? info.title_en : info.title) : t("cateringSection.title1");
  const highlight = info ? (lang === "en" && info.highlight_en ? info.highlight_en : info.highlight) : t("cateringSection.highlight");
  const subtitle = info ? (lang === "en" && info.subtitle_en ? info.subtitle_en : info.subtitle) : t("cateringSection.subtitle");
  const cta = info ? (lang === "en" && info.cta_en ? info.cta_en : info.cta) : t("cateringSection.cta");
  const img1 = info?.image1 || "https://images.unsplash.com/photo-1467003909585-2f8a72700288";
  const img2 = info?.image2 || "https://images.unsplash.com/photo-1616671285410-2a676a9a433d";

  const items = data
    ? data.items.map((it) => ({ icon: it.icon, title: lang === "en" && it.title_en ? it.title_en : it.title, desc: lang === "en" && it.desc_en ? it.desc_en : it.desc }))
    : (t("cateringSection.items") || []).map((it) => ({ icon: "heart", title: it.title, desc: it.desc }));

  return (
    <section id="catering" className="relative py-24 md:py-32 bg-[#FBF7F0] overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <div className="relative reveal">
            <div className="rounded-[2px] overflow-hidden shadow-2xl">
              <img src={img1} alt="Catering" className="w-full h-[480px] object-cover" />
            </div>
            <div className="absolute -bottom-8 -right-4 md:-right-8 w-44 h-52 md:w-56 md:h-60 rounded-[2px] overflow-hidden shadow-2xl border-4 border-[#FBF7F0] floaty">
              <img src={img2} alt="Evento" className="w-full h-full object-cover" />
            </div>
          </div>

          <div className="reveal">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-[#C9A24B]" />
              <span className="text-[#B0863B] tracking-[0.3em] text-xs uppercase">{badge}</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-[#23201C] leading-tight mb-6">
              {title} <span className="italic text-[#C9A24B]">{highlight}</span>
            </h2>
            <p className="text-[#5a5347] text-lg leading-relaxed mb-9 max-w-xl">{subtitle}</p>

            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6 mb-10">
              {items.map((it, i) => {
                const Icon = ICON_MAP[it.icon] || Heart;
                return (
                  <div key={i} className="flex items-start gap-3">
                    <div className="mt-0.5 w-10 h-10 rounded-full bg-[#C9A24B]/12 flex items-center justify-center flex-shrink-0">
                      <Icon size={18} className="text-[#C9A24B]" strokeWidth={1.6} />
                    </div>
                    <div>
                      <h3 className="font-display text-lg text-[#23201C] leading-tight">{it.title}</h3>
                      <p className="text-sm text-[#8a7f6b] mt-0.5 leading-relaxed">{it.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <a href="#prenota" className="inline-flex items-center gap-2 bg-[#1C1815] hover:bg-[#2a241f] text-[#C9A24B] px-8 py-4 rounded-full font-medium tracking-wide transition-all duration-300">
              {cta}
              <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
